/**
 * MajorationsHistoriqueSection — V36.57
 *
 * Affiche l'historique des taux de majoration personnalisés appliqués
 * par membre. Source backend : GET /api/admin/majorations-historique.
 *
 * Permet au Super Admin de tracer les décisions tarifaires, voir quels
 * membres ont une majoration active, et identifier rapidement les
 * arbitrages expirés à renouveler.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  History, Loader2, RefreshCw, Building2, Mail, CheckCircle, Clock, AlertCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { getAuthHeader } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

const STATUS_BADGE = {
  active:  { label: 'ACTIVE',  classes: 'bg-emerald-600 text-white border-emerald-700', icon: CheckCircle },
  expired: { label: 'EXPIRÉE', classes: 'bg-amber-500 text-white border-amber-600',     icon: AlertCircle },
};

export const MajorationsHistoriqueSection = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | expired

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/admin/majorations-historique?limit=${PAGE_SIZE}&offset=0`,
        { headers: getAuthHeader() },
      );
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chargement de l\'historique impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = statusFilter === 'all'
    ? items
    : items.filter((i) => i.status === statusFilter);
  const countActive  = items.filter((i) => i.status === 'active').length;
  const countExpired = items.filter((i) => i.status === 'expired').length;

  return (
    <Card data-testid="majorations-historique-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <History className="h-6 w-6 text-violet-500" />
              Historique des majorations par membre
            </CardTitle>
            <CardDescription className="mt-1">
              Tous les membres ayant reçu un <strong>taux de majoration personnalisée</strong> appliqué
              par un Super Admin. Source : décisions d'arbitrage approuvées + renouvellements.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-violet-700 text-white border border-violet-900 text-sm px-3 py-1 font-semibold">
              {total} membre{total > 1 ? 's' : ''}
            </Badge>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}
              data-testid="mh-refresh-btn">
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              <span className="ml-1.5">Rafraîchir</span>
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filtres par statut */}
        <div className="flex flex-wrap items-center gap-1.5 mb-3 pb-3 border-b border-slate-100">
          <span className="text-[11px] text-slate-500 font-semibold mr-1">Filtrer :</span>
          {[
            { k: 'all',     l: `Tous · ${items.length}`,  active: 'bg-slate-800 text-white border-slate-800' },
            { k: 'active',  l: `Actives · ${countActive}`, active: 'bg-emerald-600 text-white border-emerald-700' },
            { k: 'expired', l: `Expirées · ${countExpired}`, active: 'bg-amber-500 text-white border-amber-600' },
          ].map((f) => (
            <button key={f.k} type="button"
              onClick={() => setStatusFilter(f.k)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === f.k ? f.active : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
              data-testid={`mh-filter-${f.k}`}
            >
              {f.l}
            </button>
          ))}
        </div>

        {loading && filtered.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-500">
            <div className="w-14 h-14 rounded-full bg-violet-100 flex items-center justify-center mb-3">
              <History className="h-7 w-7 text-violet-600" />
            </div>
            <p className="font-medium text-slate-700">Aucune majoration enregistrée</p>
            <p className="text-xs text-slate-500 mt-1">
              Les taux personnalisés appliqués depuis le simulateur apparaîtront ici.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Membre</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Coefficient</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Approuvé</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Validité</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Renouv.</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600 text-xs uppercase tracking-wide">Statut</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m, idx) => {
                  const stMeta = STATUS_BADGE[m.status] || STATUS_BADGE.active;
                  const StIcon = stMeta.icon;
                  return (
                    <tr
                      key={m.membre_id}
                      className={`border-b border-slate-100 hover:bg-violet-50/30 transition-colors ${idx % 2 ? 'bg-slate-50/40' : ''}`}
                      data-testid={`mh-row-${m.membre_id}`}
                    >
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-slate-800 flex items-center gap-1.5">
                          <Building2 className="h-3.5 w-3.5 text-slate-400" />
                          {m.entreprise || '—'}
                        </div>
                        {(m.prenom || m.nom) && (
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            {`${m.prenom || ''} ${m.nom || ''}`.trim()}
                          </div>
                        )}
                        {m.email && (
                          <div className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" /> {m.email}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className="inline-block px-2.5 py-1 bg-violet-100 text-violet-800 border border-violet-300 rounded-md font-mono font-bold text-sm"
                          data-testid={`mh-coef-${m.membre_id}`}
                        >
                          × {Number(m.coefficient_libre || 0).toFixed(2)}
                        </span>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {`+${(((m.coefficient_libre || 1) - 1) * 100).toFixed(0)}%`}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-slate-400" /> {formatDate(m.approved_at)}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">par {m.approved_by || '—'}</div>
                        {m.approved_dossiers_count > 1 && (
                          <div className="text-[10px] text-slate-400 italic mt-0.5">
                            {m.approved_dossiers_count} dossiers cumulés
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs whitespace-nowrap">
                        {formatDate(m.valid_until)}
                      </td>
                      <td className="px-3 py-2.5 text-center text-slate-600 text-xs">
                        {m.renewal_count > 0 ? (
                          <span className="inline-block bg-sky-100 text-sky-700 border border-sky-200 rounded-full px-2 py-0.5 font-semibold">
                            ×{m.renewal_count}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge className={`gap-1 border ${stMeta.classes} text-[10px] px-2 py-0.5 font-bold`}>
                          <StIcon className="h-3 w-3" />
                          {stMeta.label}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MajorationsHistoriqueSection;
